/*
 * Lucid: Wartelisten-Geschenke automatisch in den Warenkorb.
 *
 * WAS VERSPROCHEN IST (tier_config der Warteliste, Stand 16.10.2026)
 *   Inner Circle (30 %)    Metalldose bei der ersten Bestellung
 *   Founder Circle (40 %)  zusaetzlich die Cap, Farbe waehlt der Kunde
 *   Secret Club (50 %)     beides (das "gestoerte Goodie" ist noch offen)
 * Franz: "Praeferiert waere ja eigentlich, wenn der Kunde ganz klar als
 * kostenloses Line Item die Dose bzw. die Cap sieht." Und 0-EUR-Bestellungen:
 * "klares Nein".
 *
 * WARUM DAS THEME DAS MACHT UND KEIN RABATT
 * Am 15. und 16.10. mit draftOrderCalculate gemessen: Ein Gratis-Code fuer
 * die Dose (Produktrabatt) neben dem Launch-Code (Bestellrabatt, mit nichts
 * kombinierbar) verwirft Shopify; BXGY genauso. Die Launch-Codes umzubauen
 * haette 0-EUR-Bestellungen erlaubt. Bleibt: Die Geschenke sind eigene,
 * nicht gelistete Produkte mit Preis 0 (snippets/lucid-gifts.liquid), und
 * dieses Modul legt sie dazu bzw. nimmt sie wieder heraus.
 *
 * DIE REGEL (planGiftUpdates)
 * - Code: In discount_codes mit applicable: true UND als discount_code mit
 *   value_type 'percentage' in cart_level_discount_applications (Bestell-
 *   rabatt) ODER in line_level_discount_allocations einer Zeile (Produkt-
 *   rabatt). Seit #1031 (16.10.2026: Metalldose allein mit 30 % plus Gratis-
 *   Dose) sind die Launch-Codes Produktrabatte nur auf Pouches, Starterset
 *   und Box; beide Formen bleiben erkannt, damit der Uebergang nichts
 *   verliert. Der Prozentsatz steht nur dort - der Code selbst
 *   (LUCID-XXXXXX) traegt ihn nicht im Namen. Dazu muss er mit einem der
 *   Praefixe aus den Theme-Einstellungen beginnen (LUCID-, LUCIDTEST-):
 *   WILLKOMMEN10 und kuenftige Codes anderer Herkunft loesen nichts aus,
 *   egal wie hoch.
 * - Pouch-Zeile: kein Geschenk, Produkt-Handle in der Theme-Einstellung
 *   "Geschenke nur mit diesen Produkten" (Pouches, Starterset, 4er/6er/8er
 *   Box) und original_line_price > 0, also VOR Rabatten. Kindzeilen einer
 *   Build-a-Box mit 0 EUR zaehlen nicht, die Box selbst schon; eine
 *   Abo-Zeile zaehlt mit ihrem Abo-Preis. Zubehoer (Metalldose, Cap) allein
 *   loest nichts aus - Franz zu #1031: "Das soll so eigentlich nicht sein".
 *   Liegen nur noch Zubehoer und Geschenke drin, fliegen die Geschenke raus.
 *   Leere Liste = kein Produkt qualifiziert, also keine Geschenke.
 * - Je Geschenk genau eine Zeile, Menge 1, ohne Selling Plan (die Produkte
 *   haben keinen, Abo-Warenkoerbe bekommen eine Einmal-Zeile dazu). Alles
 *   andere - zweite Zeile, Menge 3 ueber einen Direktlink, Geschenk ohne
 *   Code - wird korrigiert.
 *
 * WARUM /cart/update.js MIT ABSOLUTEN MENGEN
 * updates: { <variant_id>: 1 } legt eine fehlende Variante an,
 * { <zeilen-key>: 0 } nimmt eine heraus, beides in EINER Anfrage (am
 * 16.10.2026 auf der Vorschau gemessen, auch der Farbtausch Grau -> Sand).
 * Weil die Menge absolut ist und nicht addiert wird, legen zwei Tabs, die
 * gleichzeitig nachsehen, trotzdem nur eine Dose hinein. Im selben Tab
 * laeuft immer nur ein Abgleich (#running); was waehrenddessen ankommt,
 * loest genau einen weiteren aus.
 *
 * KEINE SCHLEIFE
 * Nach einer Aenderung kommt ein CartUpdateEvent mit source 'lucid-gifts' -
 * Drawer, Warenkorbseite und Cart-Icon zeichnen sich damit neu, dieses Modul
 * ignoriert es. Zusaetzlich bricht es ab, wenn es in kurzer Zeit zu oft
 * schreiben muesste (eine App, die dagegen arbeitet).
 *
 * Der Drawer geht davon nicht auf: assets/cart-drawer.js oeffnet bei
 * 'cart:update' (Theme-Einstellung "Warenkorb automatisch oeffnen") nur,
 * wenn die Quelle nicht 'lucid-gifts' ist. Sonst sprang er beim Laden einer
 * Seite auf, sobald hier etwas zu korrigieren war.
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

export const GIFT_SOURCE = 'lucid-gifts';

const CONFIG_ID = 'lucid-gift-config';
const CAP_STORAGE_KEY = 'lucid-gift-cap';
// Mehr als drei Runden braucht kein Warenkorb: erst falsche Zeilen raus,
// dann fehlende rein, dann ist er ruhig.
const MAX_ROUNDS = 3;
// Notbremse gegen ein Hin und Her mit einer anderen App.
const BREAKER_WINDOW_MS = 20000;
const BREAKER_MAX_WRITES = 8;
// Wartet der Checkout-Knopf auf einen laufenden Abgleich, dann hoechstens so lange.
const CHECKOUT_WAIT_MS = 4000;

/**
 * @typedef {{ key: string, min: number, variants: number[], default: number, free?: boolean }} GiftConfig
 * @typedef {{ check: boolean, prefixes: string[], qualifying: string[], gifts: GiftConfig[], probe: number, country: string }} Config
 */

/** @returns {Config | null} */
function readConfig() {
  const el = document.getElementById(CONFIG_ID);
  if (!el) return null;
  try {
    const raw = JSON.parse(el.textContent || '');
    const prefixes = (Array.isArray(raw.prefixes) ? raw.prefixes : [])
      .map((p) => String(p).trim().toUpperCase())
      .filter(Boolean);
    const gifts = (Array.isArray(raw.gifts) ? raw.gifts : [])
      .map((g) => ({
        key: String(g.key),
        min: Number(g.min),
        variants: (Array.isArray(g.variants) ? g.variants : []).map(Number).filter(Number.isFinite),
        default: Number(g.default),
        free: g.free !== false,
      }))
      // Kostet ein Geschenk in diesem Markt etwas (vergessener Festpreis in
      // einer neuen Preisliste), wird es gar nicht erst angeboten - lieber
      // keine Dose als eine, die der Kunde bezahlt.
      .filter((g) => g.free && g.variants.length > 0 && Number.isFinite(g.min) && g.min > 0);
    const qualifying = (Array.isArray(raw.qualifying) ? raw.qualifying : [])
      .map((h) => String(h).trim().toLowerCase())
      .filter(Boolean);
    if (prefixes.length === 0 || gifts.length === 0) return null;
    const probe = Number(raw.probe) || 0;
    const country = typeof raw.country === 'string' && /^[A-Z]{2}$/.test(raw.country) ? raw.country : '';
    return { check: Boolean(raw.check), prefixes, qualifying, gifts, probe, country };
  } catch (e) {
    return null;
  }
}

/**
 * Hoechster Prozentsatz eines angewendeten Geschenk-Codes, 0 wenn keiner.
 * @param {any} cart
 * @param {string[]} prefixes
 */
export function giftPercent(cart, prefixes) {
  const applicable = new Set(
    (cart.discount_codes || [])
      .filter((d) => d && d.applicable === true)
      .map((d) => String(d.code || '').trim().toUpperCase())
  );
  let best = 0;
  /** @param {any} app */
  const consider = (app) => {
    if (!app || app.type !== 'discount_code' || app.value_type !== 'percentage') return;
    const code = String(app.title || '').trim().toUpperCase();
    if (!applicable.has(code)) return;
    if (!prefixes.some((prefix) => code.startsWith(prefix))) return;
    const value = parseFloat(app.value);
    if (Number.isFinite(value) && value > best) best = value;
  };
  // Zwei Formen, am 16.10.2026 auf der Vorschau gesehen: /cart.js liefert die
  // Anwendung flach ({ type, title, value, ... }), /cart/update.js und
  // /cart/change.js verschachtelt ({ amount, discount_application: {...} }).
  for (const entry of cart.cart_level_discount_applications || []) consider(entry?.discount_application ?? entry);
  for (const item of cart.items || []) {
    for (const entry of item.line_level_discount_allocations || []) consider(entry?.discount_application ?? entry);
  }
  return best;
}

/**
 * Liegt eine bezahlte Pouch-Zeile (Pouches, Starterset, Box) im Warenkorb?
 * Geschenke selbst, 0-EUR-Kindzeilen einer Box und Zubehoer zaehlen nicht.
 * @param {any} cart
 * @param {Config} config
 */
export function hasQualifyingLine(cart, config) {
  const items = Array.isArray(cart.items) ? cart.items : [];
  const giftVariants = new Set(config.gifts.flatMap((g) => g.variants));
  const qualifying = new Set(config.qualifying);
  return items.some(
    (/** @type {any} */ item) =>
      !giftVariants.has(Number(item.variant_id)) &&
      qualifying.has(String(item.handle || '').toLowerCase()) &&
      Number(item.original_line_price) > 0
  );
}

/**
 * Was muss sich aendern? Reine Funktion, damit sie sich ohne Shop pruefen
 * laesst. Gibt die updates fuer /cart/update.js zurueck (leer = alles gut).
 *
 * Welche Variante (Farbe) bleibt: die gerade angetippte, sonst die schon im
 * Warenkorb liegende, sonst die zuletzt gewaehlte, sonst die vorgewaehlte.
 *
 * @param {any} cart - Warenkorb wie /cart.js
 * @param {Config} config
 * @param {{ [giftKey: string]: number }} [wanted] - eben angetippte Variante je Geschenk
 * @param {{ [giftKey: string]: number }} [prior] - frueher gewaehlte Variante je Geschenk
 * @param {Set<string>} [priced] - Geschenke, die in diesem Warenkorb etwas kosten: nur noch raus
 * @returns {Record<string, number>}
 */
export function planGiftUpdates(cart, config, wanted = {}, prior = {}, priced = new Set()) {
  const items = Array.isArray(cart.items) ? cart.items : [];
  const percent = hasQualifyingLine(cart, config) ? giftPercent(cart, config.prefixes) : 0;

  /** @type {Record<string, number>} */
  const updates = {};
  for (const gift of config.gifts) {
    const lines = items.filter((item) => gift.variants.includes(Number(item.variant_id)));
    if (percent < gift.min || priced.has(gift.key)) {
      for (const line of lines) updates[line.key] = 0;
      continue;
    }

    const preferred = [wanted[gift.key], lines[0] && Number(lines[0].variant_id), prior[gift.key], gift.default].find(
      (id) => id && gift.variants.includes(id)
    );
    const keep = lines.find((line) => Number(line.variant_id) === preferred && !line.selling_plan_allocation);
    for (const line of lines) {
      if (line === keep) {
        if (line.quantity !== 1) updates[line.key] = 1;
      } else {
        updates[line.key] = 0;
      }
    }
    // Liegt die Variante noch in einer Zeile, die gerade rausfliegt (etwa mit
    // Selling Plan), erst in der naechsten Runde neu anlegen - sonst traefen
    // sich "Zeile auf 0" und "Variante auf 1" in derselben Anfrage.
    const blocked = lines.some((line) => Number(line.variant_id) === preferred);
    if (!keep && !blocked && preferred) updates[String(preferred)] = 1;
  }
  return updates;
}

const config = readConfig();

/**
 * Welche Stufe ein Prozentsatz ausloest - dieselben Schwellen wie
 * planGiftUpdates (min je Geschenk, nur Geschenke, die hier 0 kosten).
 * @param {number} percent
 * @returns {'cap' | 'can' | null}
 */
function hintForPercent(percent) {
  if (!config) return null;
  const offered = config.gifts.filter((g) => percent >= g.min).map((g) => g.key);
  if (offered.includes('cap')) return 'cap';
  if (offered.includes('can')) return 'can';
  return null;
}

/* ---- Stufe eines Codes bei leerem Warenkorb ----
 *
 * Franz, 17.10.2026: Nach dem /discount/CODE-Link soll schon die Buy-Box
 * sagen, ob die Dose gratis dabei ist ("nämlich weil sie ein Geschenk war").
 * Der Warenkorb ist dann meist leer, und Shopify verraet den Prozentsatz
 * erst an einer echten Zeile. Die Codes (LUCID-XXXXXX) tragen ihn nicht im
 * Namen, und die Mails mit den Links sind raus - ein Parameter vom
 * Waitlist-Server kaeme fuer sie zu spaet.
 *
 * Deshalb fragt das Theme Shopify selbst: ein WEGWERF-Warenkorb ueber die
 * Storefront API (tokenlos, gleicher Host, am 17.10.2026 gemessen) mit einer
 * Pouch-Variante und dem Code. Shopify rechnet ihn genau so wie an der Kasse
 * und meldet applicable plus den Prozentsatz. Der Warenkorb des Kunden
 * bleibt unberuehrt, der Wegwerf-Warenkorb verfaellt ungenutzt.
 *
 * ZWEI FALLEN, BEIDE UMGANGEN
 * - Analytics: Shopifys Pixel-Manager umwickelt window.fetch und meldet jede
 *   Storefront-API-Antwort mit extensions.cart_changelog als "Added Product"
 *   (product_added_to_cart -> Meta-Pixel AddToCart). Eine Abfrage auf der
 *   Seite saehe aus wie ein Kunde, der eine Dose in den Warenkorb legt. Sie
 *   laeuft deshalb in einem Web Worker: dessen fetch ist nicht umwickelt.
 * - Cookies: Die Antwort setzt _shopify_essential & Co. credentials: 'omit'
 *   haelt sie aus dem Browser des Kunden heraus.
 *
 * ERGEBNIS
 *   Zahl     Shopify hat geantwortet: Prozentsatz, 0 = Code gilt nicht
 *            (ungueltig, verbraucht, abgelaufen, kein Prozent-Code).
 *   null     keine Antwort (kein Worker, Netz, Zeitlimit, Pouch nicht
 *            lieferbar) - der Hinweis bleibt beim vorsichtigen 'maybe'.
 * Ein Ergebnis gilt 15 Minuten je Code (sessionStorage), damit nicht jede
 * Seite neu fragt.
 */
const PROBE_API = '/api/2026-07/graphql.json';
const PROBE_STORAGE_KEY = 'lucid-gift-probe';
const PROBE_TTL_MS = 15 * 60 * 1000;
const PROBE_TIMEOUT_MS = 6000;
const PROBE_QUERY = `mutation LucidGiftProbe($lines: [CartLineInput!]!, $codes: [String!], $country: CountryCode) {
  cartCreate(input: { lines: $lines, discountCodes: $codes, buyerIdentity: { countryCode: $country } }) {
    cart {
      discountCodes { code applicable }
      discountAllocations { ...LucidGiftAllocation }
      lines(first: 1) { nodes { discountAllocations { ...LucidGiftAllocation } } }
    }
  }
}
fragment LucidGiftAllocation on CartDiscountAllocation {
  ... on CartCodeDiscountAllocation { code }
  discountApplication { value { ... on PricingPercentageValue { percentage } } }
}`;
// Laeuft als Blob-Worker (gleicher Ursprung wie die Seite, kein eigenes Asset,
// das auf einer Vorschau-Domain fremd waere).
const PROBE_WORKER = `self.onmessage = async (event) => {
  const { url, body } = event.data || {};
  try {
    const res = await fetch(url, { method: 'POST', credentials: 'omit', cache: 'no-store', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body });
    self.postMessage(res.ok ? await res.json() : null);
  } catch (e) {
    self.postMessage(null);
  }
};`;

/**
 * Prozentsatz aus der Antwort von cartCreate. Reine Funktion.
 * @param {any} data - JSON-Antwort der Storefront API
 * @param {string} code
 * @returns {number | null} null = nicht auswertbar
 */
export function probePercent(data, code) {
  const cart = data && data.data && data.data.cartCreate && data.data.cartCreate.cart;
  // Ohne Zeile (Variante nicht lieferbar) sagt applicable nichts.
  if (!cart || !cart.lines || !Array.isArray(cart.lines.nodes) || cart.lines.nodes.length === 0) return null;
  const wanted = String(code).trim().toUpperCase();
  const same = (/** @type {any} */ value) => String(value || '').trim().toUpperCase() === wanted;
  const applicable = (cart.discountCodes || []).some((/** @type {any} */ d) => d && same(d.code) && d.applicable === true);
  if (!applicable) return 0;
  let best = 0;
  /** @param {any} allocation */
  const consider = (allocation) => {
    if (!allocation || !same(allocation.code)) return;
    const value = Number(allocation.discountApplication && allocation.discountApplication.value && allocation.discountApplication.value.percentage);
    if (Number.isFinite(value) && value > best) best = value;
  };
  (cart.discountAllocations || []).forEach(consider);
  for (const line of cart.lines.nodes) (line.discountAllocations || []).forEach(consider);
  return best;
}

/** @type {Map<string, number | null>} */
const probed = new Map();
/** @type {Set<string>} */
const probing = new Set();

/** @param {string} code @returns {number | undefined} */
function storedProbe(code) {
  try {
    const raw = JSON.parse(sessionStorage.getItem(PROBE_STORAGE_KEY) || 'null');
    if (raw && raw.code === code && Number.isFinite(raw.percent) && Date.now() - raw.at < PROBE_TTL_MS) return raw.percent;
  } catch (e) {
    // Privates Fenster o. Ae.: dann eben neu fragen.
  }
  return undefined;
}

/** @param {string} code @param {number} percent */
function storeProbe(code, percent) {
  try {
    sessionStorage.setItem(PROBE_STORAGE_KEY, JSON.stringify({ code, percent, at: Date.now() }));
  } catch (e) {
    // Nicht speicherbar: gilt nur fuer diese Seite.
  }
}

/**
 * Fragt Shopify im Worker nach dem Prozentsatz eines Codes.
 * @param {string} code
 * @returns {Promise<number | null>}
 */
function probeGiftPercent(code) {
  if (!config || !config.probe || typeof Worker !== 'function' || typeof Blob !== 'function') return Promise.resolve(null);
  return new Promise((resolve) => {
    /** @type {Worker | null} */
    let worker = null;
    /** @type {string} */
    let url = '';
    let done = false;
    /** @param {number | null} value */
    const finish = (value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (worker) worker.terminate();
      if (url) URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), PROBE_TIMEOUT_MS);
    try {
      url = URL.createObjectURL(new Blob([PROBE_WORKER], { type: 'text/javascript' }));
      worker = new Worker(url);
      worker.onmessage = (event) => finish(probePercent(event.data, code));
      worker.onerror = () => finish(null);
      worker.postMessage({
        url: new URL(PROBE_API, window.location.origin).href,
        body: JSON.stringify({
          query: PROBE_QUERY,
          variables: {
            lines: [{ merchandiseId: `gid://shopify/ProductVariant/${config.probe}`, quantity: 1 }],
            codes: [code],
            country: config.country || null,
          },
        }),
      });
    } catch (e) {
      finish(null);
    }
  });
}

/**
 * Bekannter Prozentsatz eines Codes, oder undefined (Abfrage laeuft gerade
 * an; Ergebnis kommt als 'lucid:gift-hint' auf document).
 * @param {string} code
 * @returns {number | null | undefined}
 */
function knownPercent(code) {
  if (probed.has(code)) return probed.get(code);
  const stored = storedProbe(code);
  if (stored !== undefined) {
    probed.set(code, stored);
    return stored;
  }
  if (!probing.has(code)) {
    probing.add(code);
    probeGiftPercent(code).then((percent) => {
      probing.delete(code);
      probed.set(code, percent);
      if (percent !== null) storeProbe(code, percent);
      document.dispatchEvent(new CustomEvent('lucid:gift-hint'));
    });
  }
  return undefined;
}

/**
 * Was der Code-Hinweis in der Buy-Box (assets/lucid-code-hint.js) ueber die
 * Geschenke sagen darf - dieselbe Regel wie planGiftUpdates, damit Hinweis und
 * Warenkorb nie auseinanderlaufen. Franz, 16.10.2026: Die Nachricht, dass der
 * Kunde nichts weiter tun muss, gehoert "direkt nach dem Redirect zum Shop".
 *
 * Mit bezahlter Pouch-Zeile steht der Prozentsatz im Warenkorb selbst.
 * Ohne (nach /discount/CODE meist leer, oder nur Zubehoer drin - der Code
 * gilt nur auf Pouches, Starterset und Box) fragt knownPercent Shopify an
 * einem Wegwerf-Warenkorb. Bis die Antwort da ist: 'pending' (kein Satz).
 * Ohne Antwort: 'maybe' - Code mit Geschenk-Praefix, Stufe unbekannt, die
 * 15-%-Codes haben dasselbe Praefix und kein Geschenk.
 *
 * @param {any} cart - Warenkorb wie /cart.js
 * @returns {'cap' | 'can' | 'maybe' | 'pending' | null}
 */
export function giftHint(cart) {
  if (!config || !cart) return null;
  if (hasQualifyingLine(cart, config)) return hintForPercent(giftPercent(cart, config.prefixes));
  const codes = (cart.discount_codes || []).map((d) => String((d && d.code) || '').trim().toUpperCase());
  const last = codes.filter(Boolean).pop();
  if (!last || !config.prefixes.some((prefix) => last.startsWith(prefix))) return null;
  const percent = knownPercent(last);
  if (percent === undefined) return 'pending';
  if (percent === null) return 'maybe';
  return hintForPercent(percent);
}

// Fuer assets/lucid-code-hint.js: ein klassisches Skript ohne import.
/** @type {any} */ (window).lucidGiftHint = giftHint;

/** @type {Promise<any> | null} */
let running = null;
let again = false;
// Section-IDs, die Aufrufer zusaetzlich gerendert haben wollen (cart-discount.js).
/** @type {Set<string>} */
const extraSections = new Set();
/** @type {{ [giftKey: string]: number }} */
const wanted = {};
/** @type {number[]} */
const writes = [];
let broken = false;
// Geschenke, die hier schon einmal mit Preis > 0 im Warenkorb lagen (Markt ohne
// 0-Festpreis). Fuer den Rest des Besuchs nur noch herausnehmen, nie anlegen -
// sonst: anlegen, kostet etwas, raus, anlegen ...
/** @type {Set<string>} */
const pricedGifts = new Set();
// Der Checkout-Knopf wird nach dem Warten selbst noch einmal ausgeloest -
// dieses eine Mal nicht abfangen.
let letCheckoutThrough = false;

/** @returns {{ [giftKey: string]: number }} */
function remembered() {
  try {
    const cap = Number(localStorage.getItem(CAP_STORAGE_KEY));
    return cap ? { cap } : {};
  } catch (e) {
    return {};
  }
}

function cartItemsSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids];
}

function setBusy(/** @type {boolean} */ busy) {
  for (const el of document.querySelectorAll('cart-items-component')) {
    el.classList.toggle('cart-items-disabled', busy);
  }
}

async function readCart() {
  const res = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

/**
 * Ein Abgleich: lesen, planen, schreiben, bis nichts mehr zu tun ist.
 * @param {any} [startCart] - frischer Warenkorb, falls der Aufrufer ihn schon hat
 */
async function reconcile(startCart) {
  if (!config || broken) return { changed: false, cart: startCart ?? null, sections: {} };

  let cart = startCart && Array.isArray(startCart.items) ? startCart : await readCart();
  /** @type {Record<string, string>} */
  let sections = {};
  let changed = false;
  // Nur die Farbwahl verbrauchen, die dieser Abgleich auch gesehen hat; ein
  // Tipp waehrend er laeuft, gilt fuer den naechsten.
  const wish = { ...wanted };

  for (let round = 0; round < MAX_ROUNDS; round++) {
    for (const gift of config.gifts) {
      const costs = (cart.items || []).some(
        (/** @type {any} */ item) =>
          gift.variants.includes(Number(item.variant_id)) && Number(item.original_line_price) > 0
      );
      if (costs) pricedGifts.add(gift.key);
    }
    const updates = planGiftUpdates(cart, config, wish, remembered(), pricedGifts);
    if (Object.keys(updates).length === 0) break;

    const now = Date.now();
    while (writes.length && now - writes[0] > BREAKER_WINDOW_MS) writes.shift();
    if (writes.length >= BREAKER_MAX_WRITES) {
      broken = true;
      console.warn('[lucid-gifts] Zu viele Aenderungen in kurzer Zeit - Abgleich fuer diese Seite angehalten.');
      break;
    }
    writes.push(now);

    const sectionIds = [...new Set([...cartItemsSectionIds(), ...extraSections])];
    setBusy(true);
    try {
      const res = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        cart = data;
        sections = { ...sections, ...(data.sections || {}) };
        changed = true;
      } else {
        // Typisch: ein Zeilen-Key hat sich zwischen Lesen und Schreiben
        // geaendert (Shopify vergibt neue Keys, wenn ein Code dazukommt).
        // Frisch lesen und neu planen.
        cart = await readCart();
      }
    } finally {
      setBusy(false);
    }
  }

  for (const key of Object.keys(wish)) {
    if (wanted[key] === wish[key]) delete wanted[key];
  }
  if (changed) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, GIFT_SOURCE, {
        source: GIFT_SOURCE,
        itemCount: cart.item_count,
        sections,
      })
    );
  }
  return { changed, cart, sections };
}

/**
 * Abgleich anstossen. Laeuft schon einer, haengt er eine Runde mit frisch
 * gelesenem Warenkorb an, und der Aufrufer bekommt das Gesamtergebnis.
 *
 * @param {{ cart?: any, sections?: string[] }} [options]
 *   cart: frischer Warenkorb, spart das Lesen (nur genutzt, wenn gerade nichts laeuft)
 *   sections: zusaetzliche Section-IDs, die in der Antwort mitgerendert werden sollen
 * @returns {Promise<{ changed: boolean, cart: any, sections: Record<string, string> }>}
 */
export function syncGifts(options = {}) {
  if (!config) return Promise.resolve({ changed: false, cart: options.cart ?? null, sections: {} });

  for (const id of options.sections || []) extraSections.add(id);
  if (running) {
    again = true;
    return running;
  }

  running = (async () => {
    /** @type {{ changed: boolean, cart: any, sections: Record<string, string> }} */
    let result = { changed: false, cart: options.cart ?? null, sections: {} };
    let startCart = options.cart;
    try {
      do {
        again = false;
        const next = await reconcile(startCart);
        startCart = undefined;
        result = {
          changed: result.changed || next.changed,
          cart: next.cart,
          sections: { ...result.sections, ...next.sections },
        };
      } while (again);
    } catch (e) {
      // Kein Warenkorb lesbar: nichts anfassen. Der naechste 'cart:update'
      // versucht es wieder.
    } finally {
      running = null;
      extraSections.clear();
    }
    return result;
  })();
  return running;
}

if (config) {
  // Jede Aenderung am Warenkorb - ausser der eigenen.
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const data = /** @type {any} */ (event).detail?.data;
    if (data && data.source === GIFT_SOURCE) return;
    syncGifts();
  });

  // Code im Warenkorb eingegeben oder entfernt (assets/cart-discount.js).
  // Dort wird schon VOR diesem Event abgeglichen; hier faellt das nur noch
  // auf, wenn es jemand anderes ausloest. detail.resource ist der frische
  // Warenkorb aus /cart/update.js.
  document.addEventListener(ThemeEvents.discountUpdate, (event) => {
    const resource = /** @type {any} */ (event).detail?.resource;
    syncGifts({ cart: resource && Array.isArray(resource.items) ? resource : undefined });
  });

  // Farbe der Cap: Knopf an der Geschenkzeile (snippets/cart-products.liquid).
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-lucid-gift-variant]');
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    if (button.getAttribute('aria-pressed') === 'true') return;
    const id = Number(button.dataset.lucidGiftVariant);
    const gift = config.gifts.find((g) => g.variants.includes(id));
    if (!gift) return;
    wanted[gift.key] = id;
    if (gift.key === 'cap') {
      try {
        localStorage.setItem(CAP_STORAGE_KEY, String(id));
      } catch (e) {
        // Privates Fenster: Wahl gilt nur fuer diesen Warenkorb.
      }
    }
    const group = button.closest('[data-lucid-gift-colors]');
    group?.setAttribute('aria-busy', 'true');
    for (const other of group?.querySelectorAll('[data-lucid-gift-variant]') || []) {
      other.setAttribute('aria-pressed', String(other === button));
    }
    syncGifts().finally(() => group?.removeAttribute('aria-busy'));
  });

  // Checkout, waehrend ein Abgleich laeuft: Das Formular schickt die Mengen
  // als updates[] in Zeilenreihenfolge mit. Kommt eine Geschenkzeile erst
  // nach dem Zeichnen dazu, passten die Mengen nicht mehr zu den Zeilen -
  // also warten, neu zeichnen lassen, dann erst absenden.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
      if (letCheckoutThrough) {
        letCheckoutThrough = false;
        return;
      }
      if (!running) return;
      const submitter = /** @type {SubmitEvent} */ (event).submitter;
      event.preventDefault();
      const timeout = new Promise((resolve) => setTimeout(resolve, CHECKOUT_WAIT_MS));
      Promise.race([running, timeout]).then(() => {
        // Ein Frame Luft, damit der neu gezeichnete Warenkorb (mit den
        // passenden updates[]) im DOM steht.
        requestAnimationFrame(() => {
          const freshForm = document.getElementById('cart-form');
          const button = submitter && submitter.id ? document.getElementById(submitter.id) : null;
          if (!(freshForm instanceof HTMLFormElement)) return;
          letCheckoutThrough = true;
          freshForm.requestSubmit(button instanceof HTMLButtonElement ? button : undefined);
        });
      });
    },
    true
  );

  // Zurueck aus dem Checkout oder einem anderen Tab (BF-Cache).
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncGifts();
  });

  if (config.check) syncGifts();
}
